/**
 * Splitting a layouted body into the `{% block %}` overrides it consists of.
 *
 * A template with a `layout_key` is rendered as `"{% extends '<key>' %}\n" . body`
 * (`EmailTemplate::renderEmail`), so its body is *only* block overrides — Twig
 * discards everything outside them. Each override is therefore a region of the
 * email, which is exactly what `Block.layoutSlot` models: splitting on them turns
 * a hand-written template into blocks the canvas can show and author around,
 * instead of one opaque blob nothing can be added to.
 *
 * Nothing here throws or guesses. A body that is not cleanly override-shaped
 * returns null, and the caller keeps it verbatim.
 */

/**
 * One `{% block %}` / `{% endblock %}` pair. Whitespace-control variants (`{%-`,
 * `-%}`) are matched because Twig accepts them and hand-written bodies use them;
 * the name is the first token, and `{% endblock name %}` repeats it.
 */
const BLOCK_TAG = /\{%-?\s*(block|endblock)\b([^%]*?)-?%\}/g;

/** Twig comments carry no output, so they don't count as content. */
const TWIG_COMMENT = /\{#[\s\S]*?#\}/g;

export interface LayoutOverride {
  /** The layout region this fills — `content`, `body`, `footer`, … */
  name: string;
  /** Everything between the tags, verbatim. */
  content: string;
}

function isBlank(text: string): boolean {
  return text.replace(TWIG_COMMENT, '').trim() === '';
}

/** Whether the body contains any `{% block %}` tag at all. */
export function hasBlockOverrides(body: string): boolean {
  BLOCK_TAG.lastIndex = 0;
  return BLOCK_TAG.test(body);
}

/**
 * The body's top-level overrides in source order, or **null** when it is not
 * cleanly override-shaped — unbalanced tags, a stray `{% endblock %}`, the
 * one-line `{% block title "…" %}` form (which has no closing tag, so counting
 * depth from here on would mis-pair every tag after it), or real content sitting
 * outside every block.
 *
 * Null is not an error: it means "this body is something other than a set of
 * overrides", and the only safe thing to do with it is carry it verbatim.
 */
export function splitLayoutOverrides(body: string): LayoutOverride[] | null {
  const overrides: LayoutOverride[] = [];
  let depth = 0;
  let openName = '';
  let contentStart = 0;
  /** End of the last top-level `{% endblock %}` — text after it must be blank. */
  let outsideFrom = 0;

  BLOCK_TAG.lastIndex = 0;
  let match = BLOCK_TAG.exec(body);
  while (match !== null) {
    const args = match[2].trim();

    if (match[1] === 'block') {
      // Any argument beyond the name is the inline form; see the doc comment.
      if (args === '' || /\s/.test(args)) return null;
      if (depth === 0) {
        if (!isBlank(body.slice(outsideFrom, match.index))) return null;
        openName = args;
        contentStart = match.index + match[0].length;
      }
      depth += 1;
    } else {
      if (depth === 0) return null;
      depth -= 1;
      if (depth === 0) {
        overrides.push({
          name: openName,
          content: body.slice(contentStart, match.index),
        });
        outsideFrom = match.index + match[0].length;
      }
    }

    match = BLOCK_TAG.exec(body);
  }

  if (depth !== 0) return null;
  if (!isBlank(body.slice(outsideFrom))) return null;

  return overrides;
}
